'use client';

import { createContext, useContext, useId, type ComponentProps, type LabelHTMLAttributes, type ReactNode } from 'react';
import { Root as CheckboxRoot, Indicator as CheckboxIndicator } from '@radix-ui/react-checkbox';
import { Icon } from '@/shared-ui/atoms/icon';
import { TickIcon } from '@/shared-ui/icons/tick';

import { cn } from '@/lib/utils';

type CheckboxBaseProps = Omit<ComponentProps<typeof CheckboxRoot>, 'id' | 'disabled'>;

type CheckboxRootContentProps = CheckboxBaseProps;

type CheckboxRootBaseProps = CheckboxBaseProps & {
    id?: string;
    disabled?: boolean;
};

interface CheckboxContextValue {
    checkboxId: string;
    disabled: boolean;
    rootContentProps: CheckboxRootContentProps;
}

const CheckboxContext = createContext<CheckboxContextValue | null>(null);

function useCheckboxContext(component: string): CheckboxContextValue {
    const context = useContext(CheckboxContext);
    if (!context) {
        throw new Error(`Checkbox.${component} must be used within Checkbox.Root`);
    }
    return context;
}

export interface CheckboxRootProps extends CheckboxRootBaseProps {
    className?: string;
    children: ReactNode;
}

const Root = ({ id, disabled = false, className, children, ...props }: CheckboxRootProps) => {
    const generatedId = useId();
    const checkboxId = id ?? generatedId;
    const contextValue: CheckboxContextValue = {
        checkboxId,
        disabled,
        rootContentProps: props,
    };

    return (
        <div className={cn('inline-flex flex-row items-center gap-2', className)}>
            <CheckboxContext.Provider value={contextValue}>{children}</CheckboxContext.Provider>
        </div>
    );
};

Root.displayName = 'Checkbox.Root';

export type CheckboxLabelProps = Omit<LabelHTMLAttributes<HTMLLabelElement>, 'htmlFor'> & {
    children: ReactNode;
};

const Label = ({ className, children, ...props }: CheckboxLabelProps) => {
    const { checkboxId, disabled } = useCheckboxContext('Label');

    return (
        <label
            className={cn(
                'order-2 text-foreground font-light',
                'cursor-pointer select-none',
                disabled && 'cursor-not-allowed text-muted-foreground',
                className
            )}
            htmlFor={checkboxId}
            {...props}>
            {children}
        </label>
    );
};

Label.displayName = 'Checkbox.Label';

export interface CheckboxContentProps extends CheckboxBaseProps {
    className?: string;
    disabled?: boolean;
}

const Content = ({ className, disabled, ...props }: CheckboxContentProps) => {
    const { checkboxId, disabled: rootDisabled, rootContentProps } = useCheckboxContext('Content');

    return (
        <CheckboxRoot
            {...rootContentProps}
            {...props}
            className={cn(
                'order-1 peer size-6 shrink-0 cursor-pointer rounded-xs border-1 border-snk-black bg-background text-background shadow-xs outline-none transition-colors',
                'focus-visible:ring-[var(--focus)] focus-visible:ring-2 focus-visible:ring-offset-2',
                'data-[state=checked]:bg-foreground data-[state=checked]:text-background',
                'disabled:cursor-not-allowed disabled:border-border disabled:bg-snk-disabled',
                'disabled:data-[state=checked]:bg-snk-disabled disabled:data-[state=checked]:text-snk-disabled-foreground',
                className
            )}
            disabled={rootDisabled || disabled}
            id={checkboxId}>
            <CheckboxIndicator className="grid place-content-center text-current transition-none">
                <Icon icon={TickIcon} className="size-4" />
            </CheckboxIndicator>
        </CheckboxRoot>
    );
};

Content.displayName = 'Checkbox.Content';

const Checkbox = {
    Root,
    Label,
    Content,
} as const;

export { Checkbox };
